import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { PasswordUpdateFormComponent } from './password-update-form/password-update-form.component';
import { AuthService } from '../../auth.service';
import { ActivatedRoute, Router } from '@angular/router';
import { environment } from '../../../../environments/environment';
import { skipWhile } from 'rxjs/operators';
import { Subscription } from 'rxjs';
import { UserService } from '../../../shared/services/user.service';
import { isNullOrUndefined } from '../../../shared/utils/is-null-or-undefined';
import { AppContextService } from '../../../shared/services/app-context.service';

@Component({
  selector: 'townip-password-update',
  templateUrl: './password-update.component.html',
  styleUrls: ['./password-update.component.scss']
})
export class PasswordUpdateComponent implements OnInit, OnDestroy {
  @ViewChild(PasswordUpdateFormComponent)
  public passwordForm: PasswordUpdateFormComponent;

  public showError: boolean;
  public resetInd;
  public tempPassword: string;
  public expiredLink = false;
  public failedReset = false;
  public redirectLoginInd = false;
  public redirectLoginUrl: string;
  public currentToken: string;
  public tokenChangeListener: Subscription;
  public passwordWasReset = false;

  constructor(
    public appContext: AppContextService,
    private authService: AuthService,
    private userService: UserService,
    private router: Router,
    private route: ActivatedRoute) {
  }

  public ngOnInit(): void {
    // Indicator that this was a password reset.
    this.resetInd = this.route.snapshot.params.hasCode;

    // pre-validate the reset code!
    if (!!this.resetInd) {
      // We are processing an invite code reset password
      this.authService
        .verifyResetCode()
        .then(() => {
          // Add other processing here in the future.
          this.tokenChangeListener = this.listenForTokenChange();
        })
        .catch(() => {
          this.expiredLink = true;
        });

      return;
    }

    // We are processing a reset password link
    // Grab the temp password parsed from the invite email if one exists.
    this.tempPassword = this.authService.getTempPassword();
    // This handling is for when the user refresh the pages during the password update cycle.
    if ((isNullOrUndefined(this.tempPassword) || this.tempPassword === '')) {
      this.expiredLink = true;
    } else {
      // Start token change listener
      this.tokenChangeListener = this.listenForTokenChange();
    }
  }


  public ngOnDestroy(): void {
    if (this.tokenChangeListener) {
      this.tokenChangeListener.unsubscribe();
    }
  }

  public submitChange(): void {
    if (!this.passwordForm.isFormValid()) {
      return;
    }

    // this is coming from invite
    if (!this.resetInd) {
      this.authService
        .updatePassword(this.passwordForm.getFormValue().currentPassword, this.passwordForm.getFormValue().password)
        .then(() => {
          // MFA setup suggestion
          this.router.navigate(['/auth/mfa-setup']);
        })
        .catch(() => {
          this.failedReset = true;
        });
    } else {
      // reset password, first verify the code still valid, then confirm it.
      this.authService.verifyResetCode()
        .then(email => {
          this.authService.updatePasswordReset(email, this.passwordForm.getFormValue().password)
            .then(redirectLink => {
              this.redirectLoginUrl = redirectLink;
              this.redirectLoginInd = true;
            })
            .catch(() => {
              this.failedReset = true;
            });
        })
        .catch(() => {
          this.expiredLink = true;
        });
    }
  }

  public forgotPassword(): void {
    if (this.authService.isConnected()) {
      this.userService.logout();
    }

    this.router.navigate(['/auth/password-reset']);
  }

  public redirectLogIn(): void {
    // If we are on the account management domain, we can safely hard navigate to the actual site.
    // However if we are already on the same domain, do not hard redirect, and just route locally.
    if (environment.production &&
      !isNullOrUndefined(this.redirectLoginUrl) &&
      !this.isSameOrigin(this.redirectLoginUrl)) {
      window.location.href = this.redirectLoginUrl;
    } else { // local just route
      this.router.navigate(['/auth/login']);
    }
  }

  public forceLogout(): void {
    this.router.navigate(['/auth/logout']);
  }

  private isSameOrigin(url: string): boolean {
    return url.startsWith(window.location.origin);
  }

  private listenForTokenChange(): Subscription {
    // NOTE:
    // When the token changes, this could mean that the user has updated the password in another window.
    // We need need to listen for that and redirect the user to login as we deliberately skipped automatic
    // redirect from the auth pages.
    this.currentToken = this.authService.getIdToken();

    return this.authService
      .connectedEmitter
      .pipe(
        skipWhile(() => this.authService.getIdToken() === this.currentToken),
      )
      .subscribe(() => {
        this.expiredLink = true;
        this.passwordWasReset = true;
      });
  }
}
